'use client';

//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import 'driver.js/dist/driver.css';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AllowedButtons, driver, Popover } from 'driver.js';
import type { Driver } from 'driver.js';
import { useAppNavigation } from '@/domains/navigation/use-app-navigation';
import { useLocalStorageState } from '@/hooks/useSessionState';
import type { TourStep } from './tour-step';
import { waitForElement, waitForVisibleElement } from './tour-utils';
import { getShellTourSteps, renderShellTourPopoverChrome, type ShellTourOptions } from './shell-tour-steps';

type TourStatus = null | 'skipped' | 'done';

export interface TourContextValue {
  tourStatus: TourStatus;
  activeStep: number | null;
  isActive: boolean;
  startTour: (options: ShellTourOptions) => void;
  skipTour: () => void;
  endTour: () => void;
}

const STORAGE_KEY = 'nebuly_tour_v2';

export const TourContext = createContext<TourContextValue>({
  tourStatus: null,
  activeStep: null,
  isActive: false,
  startTour: () => {},
  skipTour: () => {},
  endTour: () => {},
});

function getCurrentPathname(): string {
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return '';
}

function clearTourForceShowAttributes(): void {
  document.querySelectorAll('[data-force-show]').forEach((element) => {
    element.removeAttribute('data-force-show');
  });
}

function pathnameAligned(pathname: string, routePrefix: string): boolean {
  return pathname.startsWith(routePrefix);
}

async function waitForPathnamePrefix(
  routePrefix: string,
  options: {
    getPathname: () => string;
    isFresh: () => boolean;
    timeoutMs?: number;
    pollMs?: number;
  },
): Promise<boolean> {
  const { getPathname, isFresh, timeoutMs = 8000, pollMs = 50 } = options;
  if (pathnameAligned(getPathname(), routePrefix)) return true;

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (!isFresh()) return false;
    if (pathnameAligned(getPathname(), routePrefix)) return true;
    await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
  }
  return pathnameAligned(getPathname(), routePrefix);
}

function buildPopoverBase(
  step: TourStep,
  options: {
    isLastStep: boolean;
    isFirstStep: boolean;
    totalSteps: number;
    stepIndex: number;
    onNextClick: () => void | Promise<void>;
    onPrevClick: () => void | Promise<void>;
    onCloseClick: () => void;
  },
): Popover {
  const { isLastStep, isFirstStep, totalSteps, stepIndex, onNextClick, onPrevClick, onCloseClick } = options;

  return {
    title: step.title,
    description: step.description,
    side: step.side,
    align: step.align,
    showButtons: ['next', 'previous', 'close'] as AllowedButtons[],
    disableButtons: isFirstStep ? (['previous'] as AllowedButtons[]) : [],
    nextBtnText: isLastStep ? 'Done' : 'Next',
    prevBtnText: 'Back',
    popoverClass: 'nebuly-tour-popover nebuly-shell-tour-popover',
    onNextClick,
    onPrevClick,
    onCloseClick,
    onPopoverRender: (popover) => {
      renderShellTourPopoverChrome(
        popover.footer,
        popover.footerButtons,
        popover.closeButton,
        popover.previousButton,
        totalSteps,
        stepIndex,
      );
    },
  };
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { navigateApp } = useAppNavigation();
  const [shellOptions, setShellOptions] = useState<ShellTourOptions>({
    audience: 'employee',
    isRoiEnabled: false,
    isProficiencyEnabled: false,
  });
  const tourSteps = useMemo(() => getShellTourSteps(shellOptions), [shellOptions]);

  const [tourStatus, setTourStatus] = useLocalStorageState<TourStatus>(STORAGE_KEY, null);
  const [activeStep, setActiveStep] = useState<number | null>(null);

  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const activeStepRef = useRef<number | null>(activeStep);
  activeStepRef.current = activeStep;

  const driverRef = useRef<Driver | null>(null);
  const intentionalDestroyRef = useRef(false);
  const pendingAdvanceRef = useRef<number | null>(null);
  const lastHighlightKeyRef = useRef<string | null>(null);
  const stepHighlightCompletedRef = useRef<number | null>(null);
  const skipTourRef = useRef<() => void>(() => {});

  const isActive = activeStep !== null;

  const destroyDriver = useCallback((mode: 'hard' | 'soft') => {
    if (mode === 'hard') {
      try {
        driverRef.current?.destroy();
      } catch {
        // ignore teardown failures from driver.js internals
      }
    }
    driverRef.current = null;
  }, []);

  const createDriver = useCallback(() => {
    intentionalDestroyRef.current = true;
    try {
      destroyDriver('hard');
    } finally {
      intentionalDestroyRef.current = false;
    }

    driverRef.current = driver({
      animate: true,
      overlayColor: 'rgb(9, 9, 11)',
      overlayOpacity: 0.82,
      stagePadding: 0,
      stageRadius: 0,
      popoverClass: 'nebuly-tour-popover',
      disableActiveInteraction: true,
      allowClose: true,
      onDestroyStarted: () => {
        if (intentionalDestroyRef.current) return;
        skipTourRef.current();
      },
    });
  }, [destroyDriver]);

  const stopTourInternal = useCallback(
    (status: Exclude<TourStatus, null>) => {
      pendingAdvanceRef.current = null;
      lastHighlightKeyRef.current = null;
      stepHighlightCompletedRef.current = null;
      setActiveStep(null);
      document.dispatchEvent(new CustomEvent('nebuly:tour-close-global-filter'));
      document.dispatchEvent(new CustomEvent('nebuly:tour-close-more-workspace'));
      document.dispatchEvent(new CustomEvent('nebuly:tour-close-sidebar-menus'));
      clearTourForceShowAttributes();

      intentionalDestroyRef.current = true;
      try {
        destroyDriver('hard');
      } finally {
        intentionalDestroyRef.current = false;
      }

      setTourStatus(status);
    },
    [destroyDriver, setTourStatus],
  );

  const skipTour = useCallback(() => {
    stopTourInternal('skipped');
  }, [stopTourInternal]);

  const endTour = useCallback(() => {
    stopTourInternal('done');
  }, [stopTourInternal]);

  skipTourRef.current = skipTour;

  const markStepHighlighted = useCallback((stepIndex: number) => {
    stepHighlightCompletedRef.current = stepIndex;
  }, []);

  const highlightStep = useCallback(
    async (idx: number, highlightPathname: string) => {
      if (activeStepRef.current === null) return;

      const pending = pendingAdvanceRef.current;
      if (pending === idx) {
        return;
      }

      if (pending !== null && idx > 0 && pending === idx - 1) {
        return;
      }

      const dedupeKey = `${idx}:${highlightPathname}`;
      if (lastHighlightKeyRef.current === dedupeKey) return;
      lastHighlightKeyRef.current = dedupeKey;

      const step = tourSteps[idx];
      if (!step) return;

      if (!driverRef.current) {
        createDriver();
      }

      const onSkip = () => {
        skipTour();
      };

      const isLastStep = idx === tourSteps.length - 1;
      const isFirstStep = idx === 0;
      const assertFresh = () => activeStepRef.current === idx;

      const onNextClick = async () => {
        if (activeStepRef.current !== idx) return;
        const nextIndex = idx + 1;
        if (nextIndex >= tourSteps.length) {
          endTour();
          return;
        }

        const nextStep = tourSteps[nextIndex];
        pendingAdvanceRef.current = idx;
        setActiveStep(nextIndex);

        void (async () => {
          try {
            try {
              await step.beforeNextStep?.(nextStep);
            } catch (e) {
              console.warn('[Tour] beforeNextStep failed, advancing anyway:', e);
            }
            if (activeStepRef.current !== nextIndex) return;

            const route = nextStep.route;
            if (route && !pathnameAligned(getCurrentPathname(), route)) {
              const aligned = await waitForPathnamePrefix(route, {
                getPathname: getCurrentPathname,
                isFresh: () => activeStepRef.current === nextIndex,
              });
              if (!aligned && activeStepRef.current === nextIndex) {
                router.push(route);
              }
            }
          } finally {
            if (pendingAdvanceRef.current === idx) {
              pendingAdvanceRef.current = null;
              if (activeStepRef.current === nextIndex && stepHighlightCompletedRef.current !== nextIndex) {
                lastHighlightKeyRef.current = null;
                void highlightStep(nextIndex, getCurrentPathname());
              }
            }
          }
        })();
      };

      const onPrevClick = () => {
        if (activeStepRef.current !== idx || idx === 0) return;
        lastHighlightKeyRef.current = null;
        stepHighlightCompletedRef.current = null;
        setActiveStep(idx - 1);
      };

      const popoverBase = buildPopoverBase(step, {
        isLastStep,
        isFirstStep,
        totalSteps: tourSteps.length,
        stepIndex: idx,
        onNextClick,
        onPrevClick,
        onCloseClick: onSkip,
      });
      const highlightOptions =
        step.disableActiveInteraction === undefined ? {} : { disableActiveInteraction: step.disableActiveInteraction };

      if (step.route && !pathnameAligned(getCurrentPathname(), step.route)) {
        const aligned = await waitForPathnamePrefix(step.route, {
          getPathname: getCurrentPathname,
          isFresh: assertFresh,
          timeoutMs: 8000,
          pollMs: 50,
        });
        if (!assertFresh()) return;
        if (!aligned) {
          driverRef.current?.highlight({
            ...highlightOptions,
            popover: {
              ...popoverBase,
              side: 'over',
              align: 'center',
            },
          });
          markStepHighlighted(idx);
          return;
        }
        lastHighlightKeyRef.current = null;
      }

      if (!step.element) {
        if (!assertFresh()) return;
        driverRef.current?.highlight({
          ...highlightOptions,
          popover: {
            ...popoverBase,
            side: 'over',
            align: 'center',
          },
        });
        markStepHighlighted(idx);
        return;
      }

      const el = step.element ? await waitForElement(step.element) : null;
      if (!assertFresh()) return;
      if (!el) {
        if (pendingAdvanceRef.current !== null) return;
        driverRef.current?.highlight({
          ...highlightOptions,
          popover: {
            ...popoverBase,
            side: 'over',
            align: 'center',
          },
        });
        markStepHighlighted(idx);
        return;
      }

      const highlightTarget = (await waitForVisibleElement(step.element, 10000)) ?? el;
      if (highlightTarget instanceof HTMLElement) {
        highlightTarget.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
      }

      driverRef.current?.highlight({
        ...highlightOptions,
        element: step.element,
        popover: {
          ...popoverBase,
          side: step.side ?? 'bottom',
          align: step.align ?? 'start',
        },
      });
      markStepHighlighted(idx);

      requestAnimationFrame(() => {
        driverRef.current?.refresh();
      });
    },
    [createDriver, endTour, markStepHighlighted, router, skipTour, tourSteps],
  );

  const startTourInternal = useCallback(
    async (options: ShellTourOptions) => {
      if (activeStepRef.current !== null) return;

      pendingAdvanceRef.current = null;
      lastHighlightKeyRef.current = null;
      stepHighlightCompletedRef.current = null;

      if (!pathnameAligned(pathnameRef.current, '/reports')) {
        navigateApp({ route: '/reports' });
        let reachedReports = await waitForPathnamePrefix('/reports', {
          getPathname: () => pathnameRef.current,
          isFresh: () => activeStepRef.current === null,
        });

        if (!reachedReports) {
          router.push('/reports');
          reachedReports = await waitForPathnamePrefix('/reports', {
            getPathname: () => pathnameRef.current,
            isFresh: () => activeStepRef.current === null,
            timeoutMs: 4000,
          });
          if (!reachedReports) return;
        }
      }

      createDriver();
      setShellOptions(options);
      setActiveStep(0);
    },
    [createDriver, navigateApp, router],
  );

  const startTour = useCallback(
    (options: ShellTourOptions) => {
      void startTourInternal(options);
    },
    [startTourInternal],
  );

  useEffect(() => {
    if (activeStep === null) return;

    const step = tourSteps[activeStep];
    if (!step) return;

    void highlightStep(activeStep, pathname);
  }, [activeStep, highlightStep, pathname, tourSteps]);

  useEffect(() => {
    if (activeStep === null) {
      lastHighlightKeyRef.current = null;
    }
  }, [activeStep]);

  useEffect(() => {
    return () => {
      intentionalDestroyRef.current = true;
      try {
        destroyDriver('hard');
      } finally {
        intentionalDestroyRef.current = false;
      }
    };
  }, [destroyDriver]);

  return (
    <TourContext.Provider
      value={{
        tourStatus,
        activeStep,
        isActive,
        startTour,
        skipTour,
        endTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTourContext() {
  return useContext(TourContext);
}
